import { ExpressPaymentApprovalRequest, AuthorizeExpressPaymentRequestModel, ShippingOption } from '@esw/payments-express-panel';
import { getComposableApiUrl } from '../express-payment-orchestrator';
import { ExpressPaymentDetails, Address } from '../../types/external/express-payment-component';
import {
  ESW_ERROR_CODES,
  ESWAPIError,
  ESWConnectionError,
  ESWError,
  ESWPaymentError,
  ESWStateError,
  ESWValidationError
} from './error-handling';

const {
  NETWORK_ERROR,
  PAYMENT_VALIDATION_FAILED,
  ADDRESS_VALIDATION_FAILED,
  PAYMENT_DECLINED,
  AUTHORIZE_PAYMENT_FAILED,
  INVALID_ADDRESS,
  SOMETHING_WRONG
} = ESW_ERROR_CODES;

// Local enum definitions to avoid importing from Angular package
export enum ShippingOptionEnum {
  Post = 'Post',
  Exp1 = 'Exp1',
  Exp2 = 'Exp2',
  Pudo = 'Pudo',
  Store = 'Store'
}

export enum ShippingTypeEnum {
  Shipping = 'Shipping',
  PickupInStore = 'PickupInStore',
  PickupFromPerson = 'PickupFromPerson'
}

export interface ComposableShippingOption {
  code: ShippingOptionEnum;
  label: string;
  shippingPrice: number;
  currencyIso: string;
  type: ShippingTypeEnum;
  selected?: boolean;
  isSelected: boolean;
}

interface ApprovalResult {
  approved: boolean;
  reason: string;
  authorizeRequest: AuthorizeExpressPaymentRequestModel;
}

interface PersistAndAuthoriseParams {
  expressApprovalRequest: ExpressPaymentApprovalRequest;
  shippingOptionId: string;
  tenantCode: string;
  expressPaymentId: string;
  forterToken: string | null;
}

export interface PartialAddress {
  region: string;
  city: string;
  countryIso: string;
  postalCode: string;
}

export interface AddressValidationResult {
  shippingOptions: ShippingOption[];
}

type Failures = Record<string, string[]>;

interface Failure {
  code: string;
  property: string;
  message: string;
}

/**
 * Persists express payment details and authorizes the payment.
 * @param params - Approval request, tenant, and fraud token.
 * @returns Approval result indicating success/failure with reason.
 * @throws {ESWPaymentError} When payment is declined or validation fails (400/422 response).
 * @throws {ESWAPIError} When the API returns a server error (500+) or other error status.
 * @throws {ESWConnectionError} When a network error occurs during the request.
 */
export async function persistAndAuthoriseExpressPayment({
  expressApprovalRequest,
  shippingOptionId,
  tenantCode,
  expressPaymentId,
  forterToken
}: PersistAndAuthoriseParams): Promise<ApprovalResult> {
  try {
    const expressPaymentDetails = formatDetails(expressApprovalRequest, shippingOptionId);

    const validatePaymentDetails = await fetch(`${getComposableApiUrl()}/${tenantCode}/expressPayment/${expressPaymentId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(expressPaymentDetails)
    });

    const res = await validatePaymentDetails.json();

    if (!validatePaymentDetails.ok) {
      paymentPersistenceCheckAndThrow(validatePaymentDetails.status, res.message, res.failures || [], 'persist');
    }

    if (res.totalAmount !== expressApprovalRequest.amount) {
      throw new ESWPaymentError(`Something went wrong while validating and authorizing the payment details...`, SOMETHING_WRONG);
    }

    const authorisePayment = await fetch(`${getComposableApiUrl()}/${tenantCode}/expressPayment/${expressPaymentId}/place`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        fraudData: {
          forterToken: forterToken ?? ''
        }
      })
    });

    if (!authorisePayment.ok) {
      const res = await authorisePayment.json();
      paymentPersistenceCheckAndThrow(authorisePayment.status, res.message, res.failures || [], 'authorize');
    }

    return {
      approved: true,
      reason: 'Payment approved successfully',
      authorizeRequest: {} as AuthorizeExpressPaymentRequestModel
    };
  } catch (error) {
    if (error instanceof ESWError) {
      throw error;
    }

    // Network errors: TypeError from fetch, AbortError, etc.
    if (error instanceof TypeError || (error instanceof Error && (error.name === 'AbortError' || error.name === 'NetworkError'))) {
      throw new ESWConnectionError('Network error during payment authorization', NETWORK_ERROR);
    }

    // Unexpected errors (likely bugs)
    throw new ESWStateError(
      `Unexpected error in payment authorization: ${error instanceof Error ? error.message : String(error)}`,
      AUTHORIZE_PAYMENT_FAILED
    );
  }
}

/**
 * Validates a partial address and retrieves available shipping options.
 * @param address - Partial address containing region, city, country, and postal code.
 * @param tenantCode - Tenant identifier for the API request.
 * @param expressPaymentId - Express payment session identifier.
 * @returns Promise resolving to validation result with available shipping options.
 *
 * @throws {ESWValidationError} When the address is invalid (400/422 response).
 * @throws {ESWAPIError} When the API returns a server error (500+) or other error status.
 * @throws {ESWConnectionError} When a network error occurs during the request.
 */
export async function validateAddressApi(
  address: PartialAddress,
  tenantCode: string,
  expressPaymentId: string
): Promise<AddressValidationResult> {
  try {
    const response = await fetch(`${getComposableApiUrl()}/${tenantCode}/expressPayment/${expressPaymentId}/validateAddress`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(address) // Ensure postalCode is not undefined
    });

    const data = await response.json();

    if (!response.ok) {
      addressValidationCheckAndThrow(response.status, data.message, data.failures);
    }

    return {
      shippingOptions: formatShippingOptions(data.shippingOptions) ?? []
    };
  } catch (error) {
    if (error instanceof ESWError) {
      throw error;
    }

    // Network errors: TypeError from fetch, AbortError, etc.
    if (error instanceof TypeError || (error instanceof Error && (error.name === 'AbortError' || error.name === 'NetworkError'))) {
      throw new ESWConnectionError('Network error during payment authorization', NETWORK_ERROR);
    }

    throw new ESWStateError(
      `Unexpected error in address validation: ${error instanceof Error ? error.message : String(error)}`,
      ADDRESS_VALIDATION_FAILED
    );
  }
}

function formatDetails(req: ExpressPaymentApprovalRequest, shippingOptionId: string): ExpressPaymentDetails {
  const mapAddress = (addr: Address, payer: ExpressPaymentApprovalRequest['payer']): Address => ({
    firstName: addr.firstName || payer.firstName || '',
    lastName: addr.lastName || payer.lastName || '',
    addressLine1: addr.addressLine1,
    addressLine2: addr.addressLine2 || '',
    addressLine3: '',
    city: addr.city,
    region: addr.region || '',
    postalCode: addr.postalCode,
    countryIso: addr.countryIso,
    phone: addr.phone || payer.phone || '',
    email: addr.email || payer.email || ''
  });

  const address = mapAddress(req.shippingAddress as Address, req.payer);

  return {
    billingAddress: address,
    shippingAddress: address,
    currencyIso: req.currencyIso,
    paymentAmount: req.amount,
    cultureCode: '',
    shippingOption: {
      code: shippingOptionId || ''
    }
  };
}

function formatShippingOptions(composableOptions: ComposableShippingOption[] | []): ShippingOption[] {
  if (!composableOptions?.length) {
    return [];
  }
  return composableOptions.map((option) => ({
    id: option.code,
    label: option.label,
    amount: option.shippingPrice,
    currencyIso: option.currencyIso,
    type: option.type,
    selected: option.isSelected
  }));
}

function formatErrors(failures: Failure[], convertToCamelCase = true): Failures {
  return failures.reduce((errors: Failures, failure: Failure) => {
    const { property, message } = failure;
    let key = property;
    // Property values returned in PascalCase > convert to camelCase
    if (convertToCamelCase) {
      key = property[0].toLowerCase() + property.slice(1);
    }

    if (!errors[key]) {
      errors[key] = [message];
    }
    return errors;
  }, {});
}

function addressValidationCheckAndThrow(status: number, message: string, failures: Failure[]): void {
  const errorMessage = message || `Address validation failed: ${status}`;
  if (status === 400 || status === 422) {
    // Validation error - backend rejected the address
    throw new ESWValidationError(message, INVALID_ADDRESS, formatErrors(failures));
  } else if (status >= 500) {
    // Server error
    throw new ESWAPIError(errorMessage, ADDRESS_VALIDATION_FAILED, status);
  } else {
    // Other error (401, 403, 404, etc)
    throw new ESWAPIError(errorMessage, ADDRESS_VALIDATION_FAILED, status);
  }
}

function paymentPersistenceCheckAndThrow(status: number, message: string, failures: Failure[], operation: 'persist' | 'authorize'): void {
  const operationLabel = operation === 'persist' ? 'Payment persistence' : 'Payment authorization';
  const errorMessage = message || `${operationLabel} failed: ${status}`;
  const errorCode = operation === 'persist' ? PAYMENT_VALIDATION_FAILED : PAYMENT_DECLINED;

  if (status === 400 || status === 422) {
    // Client error - payment declined, validation failed (safe to show user)
    throw new ESWPaymentError(errorMessage, errorCode, formatErrors(failures, false));
  } else if (status >= 500) {
    // Server error
    throw new ESWAPIError(errorMessage, AUTHORIZE_PAYMENT_FAILED, status, formatErrors(failures));
  }

  // Other error (401, 403, 404, etc)
  throw new ESWAPIError(errorMessage, errorCode, status, formatErrors(failures, false));
}
